// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Folder, ChevronRight, Home, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RenameModal({ isOpen, onClose, item, itemType, onSuccess }) {
  const [newName, setNewName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (item) {
      setNewName(itemType === 'file' ? item.original_filename : item.name);
    }
  }, [item, itemType]);

  const handleRename = async () => {
    if (!newName.trim()) {
      toast.error('Digite um nome válido');
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = itemType === 'file' 
        ? `${API}/files/${item.id}/rename`
        : `${API}/folders/${item.id}/rename`;
      
      await axios.put(endpoint, { new_name: newName.trim() });
      toast.success(`${itemType === 'file' ? 'Arquivo' : 'Pasta'} renomeado(a) com sucesso!`);
      onSuccess();
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao renomear';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="rename-modal">
        <DialogHeader>
          <DialogTitle>Renomear {itemType === 'file' ? 'Arquivo' : 'Pasta'}</DialogTitle>
          <DialogDescription>
            Digite o novo nome para {itemType === 'file' ? 'o arquivo' : 'a pasta'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="newName">Novo nome</Label>
            <Input
              id="newName"
              data-testid="rename-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Digite o novo nome"
              onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleRename}
            disabled={isLoading}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="rename-confirm-button"
          >
            {isLoading ? 'Renomeando...' : 'Renomear'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MoveModal({ isOpen, onClose, item, itemType, category, onSuccess }) {
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadFolders(null);
      setBreadcrumbs([]);
    }
    // loadFolders is recreated every render but only depends on isOpen/category.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, category]);

  const loadFolders = async (parentId) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ category });
      if (parentId) params.append('parent_id', parentId);
      
      const response = await axios.get(`${API}/folders?${params}`);
      
      // Filter out the item being moved (if it's a folder)
      let filteredFolders = response.data;
      if (itemType === 'folder' && item) {
        filteredFolders = response.data.filter(f => f.id !== item.id);
      }
      
      setFolders(filteredFolders);
      setCurrentFolderId(parentId);
    } catch (error) {
      toast.error('Erro ao carregar pastas');
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToFolder = async (folder) => {
    await loadFolders(folder.id);
    setBreadcrumbs([...breadcrumbs, folder]);
  };

  const navigateBack = async () => {
    const newBreadcrumbs = [...breadcrumbs];
    newBreadcrumbs.pop();
    setBreadcrumbs(newBreadcrumbs);
    
    const parentId = newBreadcrumbs.length > 0 
      ? newBreadcrumbs[newBreadcrumbs.length - 1].id 
      : null;
    await loadFolders(parentId);
  };

  const navigateToRoot = async () => {
    setBreadcrumbs([]);
    await loadFolders(null);
  };

  const handleMove = async () => {
    // Don't allow moving to current location
    const currentLocation = item.folder_id || item.parent_id;
    if (currentFolderId === currentLocation) {
      toast.error('O item já está nesta pasta');
      return;
    }

    setIsMoving(true);
    try {
      const endpoint = itemType === 'file' 
        ? `${API}/files/${item.id}/move`
        : `${API}/folders/${item.id}/move`;
      
      await axios.put(endpoint, { target_folder_id: currentFolderId });
      toast.success(`${itemType === 'file' ? 'Arquivo' : 'Pasta'} movido(a) com sucesso!`);
      onSuccess();
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao mover';
      toast.error(message);
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="move-modal">
        <DialogHeader>
          <DialogTitle>Mover {itemType === 'file' ? 'Arquivo' : 'Pasta'}</DialogTitle>
          <DialogDescription>
            Selecione a pasta de destino para "{itemType === 'file' ? item?.original_filename : item?.name}"
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Breadcrumb navigation */}
          <div className="flex items-center gap-1 text-sm text-slate-600 flex-wrap">
            <button
              onClick={navigateToRoot}
              className="flex items-center gap-1 hover:text-slate-900 p-1 rounded hover:bg-slate-100"
            >
              <Home className="w-4 h-4" />
              Raiz
            </button>
            {breadcrumbs.map((crumb, index) => (
              <div key={crumb.id} className="flex items-center">
                <ChevronRight className="w-4 h-4 text-slate-400" />
                <button
                  onClick={() => {
                    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
                    setBreadcrumbs(newBreadcrumbs);
                    loadFolders(crumb.id);
                  }}
                  className="hover:text-slate-900 p-1 rounded hover:bg-slate-100"
                >
                  {crumb.name}
                </button>
              </div>
            ))}
          </div>

          {/* Back button */}
          {breadcrumbs.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={navigateBack}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Voltar
            </Button>
          )}

          {/* Folder list */}
          <div className="border rounded-lg max-h-[300px] overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center text-slate-500">
                Carregando...
              </div>
            ) : folders.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Folder className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Nenhuma subpasta
              </div>
            ) : (
              <div className="divide-y">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => navigateToFolder(folder)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left"
                    data-testid={`folder-option-${folder.id}`}
                  >
                    <Folder className="w-5 h-5 text-amber-500" />
                    <span className="flex-1 truncate">{folder.name}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Current location info */}
          <div className="p-3 bg-slate-50 rounded-lg text-sm">
            <span className="text-slate-500">Mover para: </span>
            <span className="font-medium">
              {breadcrumbs.length === 0 
                ? 'Raiz' 
                : breadcrumbs.map(b => b.name).join(' / ')}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleMove}
            disabled={isMoving}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="move-confirm-button"
          >
            {isMoving ? 'Movendo...' : 'Mover para cá'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
